/**
 * KAN-846: менеджер, вошедший по ссылке, задаёт себе логин и пароль
 * (POST /managers/me/credentials/). Логин - телефон из профиля или почта
 * (почта обязательна, только если телефона нет). Проверка до отправки:
 * пароль задан и повторён, есть телефон или почта. Ошибки backend по полям
 * (email, password, password_confirm), прочее одной строкой над кнопкой.
 * Успех: профиль в кеше заменяется ответом (needs_credentials=false).
 */
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MY_MANAGER_PROFILE_KEY, useMyManagerProfile } from '@/hooks/useManagerProfile';
import { managersApi } from '@/services/api/managers';
import { FORM_ERROR_KEY, profileErrorsFrom, type ProfileErrors } from '@/lib/managerProfileErrors';
import { ONBOARDING_TEXTS } from '@/lib/managerOnboarding';
import type { ManagerCredentialsBody } from '@/types/manager';

const FIELDS = ['email', 'password', 'password_confirm'] as const;
export const CREDENTIALS_PASSWORD_REQUIRED = 'Придумайте пароль';
export const CREDENTIALS_MISMATCH = 'Пароли не совпадают';

export interface CredentialsDraft {
  email: string;
  password: string;
  password_confirm: string;
}

type Field = keyof CredentialsDraft;

const EMPTY: CredentialsDraft = { email: '', password: '', password_confirm: '' };

export function validateCredentialsDraft(draft: CredentialsDraft, hasPhone: boolean): ProfileErrors {
  const errors: ProfileErrors = {};
  if (!hasPhone && !draft.email.trim()) errors.email = ONBOARDING_TEXTS.credentialsNoPhone;
  if (!draft.password) errors.password = CREDENTIALS_PASSWORD_REQUIRED;
  else if (draft.password !== draft.password_confirm) errors.password_confirm = CREDENTIALS_MISMATCH;
  return errors;
}

function bodyFrom(draft: CredentialsDraft): ManagerCredentialsBody {
  const email = draft.email.trim();
  return { ...(email ? { email } : {}), password: draft.password, password_confirm: draft.password_confirm };
}

export function useMyCredentialsForm() {
  const profile = useMyManagerProfile();
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: ManagerCredentialsBody) => managersApi.setMyCredentials(body),
    onSuccess: (data) => client.setQueryData(MY_MANAGER_PROFILE_KEY, data),
  });
  const [draft, setDraft] = useState<CredentialsDraft>(EMPTY);
  const [errors, setErrors] = useState<ProfileErrors>({});
  const phone = profile.data?.phone.trim() ?? '';

  const setField = useCallback((field: Field, value: string) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setErrors((e) => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const local = validateCredentialsDraft(draft, phone !== '');
    setErrors(local);
    if (Object.keys(local).length > 0) return false;
    try {
      await mutation.mutateAsync(bodyFrom(draft));
      setDraft(EMPTY);
      return true;
    } catch (err) {
      setErrors(profileErrorsFrom(err, FIELDS));
      return false;
    }
  }, [draft, phone, mutation]);

  return {
    profile: profile.data,
    phone,
    draft,
    setField,
    save,
    saving: mutation.isPending,
    errors,
    formError: errors[FORM_ERROR_KEY] ?? null,
  };
}
